import { abortDocumentReaderUpload } from '@commenda/api/commenda-ai/document-reader/abortDocumentReaderUpload';
import { appendDocumentReaderUploadFiles } from '@commenda/api/commenda-ai/document-reader/appendDocumentReaderUploadFiles';
import { completeDocumentReaderUploadFile } from '@commenda/api/commenda-ai/document-reader/completeDocumentReaderUploadFile';
import { createDocumentReaderUpload } from '@commenda/api/commenda-ai/document-reader/createDocumentReaderUpload';
import { getDocumentReaderUploadBatch } from '@commenda/api/commenda-ai/document-reader/getDocumentReaderUploadBatch';
import { putDocumentReaderPart } from '@commenda/api/commenda-ai/document-reader/putDocumentReaderPart';
import { requestDocumentReaderPartUrls } from '@commenda/api/commenda-ai/document-reader/requestDocumentReaderPartUrls';
import {
  DocumentReaderUploadFileStatus,
  DocumentReaderUploadStatus,
  type TPutDocumentReaderPartResult,
} from '@commenda/api/commenda-ai/document-reader/types';
import type { CommendaAiUploadFile } from '@commenda/api/commenda-ai/types';

import { DataRoomUploadHelper } from './DataRoomUploadHelper';
import type {
  ICreateDataRoomParams,
  IDataRoomFileOutcome,
  IDataRoomFileProgress,
  IDataRoomLocalFile,
  IDataRoomResumeRecord,
  IDataRoomTransferContext,
  TCreateDataRoomResult,
  TDataRoomLimiter,
  TLoadDataRoomResumeResult,
} from './types';

interface IDataRoomCreateState {
  files: IDataRoomLocalFile[];
  skippedEmptyFileNames: string[];
  lastModifiedByBatch: number[][];
  error: string | null;
}

interface IDataRoomResumeMatchResult {
  files: IDataRoomLocalFile[];
  storedRows: IDataRoomFileProgress[];
  unmatchedNames: string[];
  abortedNames: string[];
}

type TPartOutcome = { entityTag: string } | { error: string; isFatal: boolean };

const CANCELLED_ERROR = 'The upload was cancelled.';
const PAIRING_ERROR =
  'The server answered with a different file list, so the upload was stopped.';

/** The network side of a data-room upload: create, part URLs, parallel PUTs with retries, completion
 * and resume. Holds no state of its own; the hook owns the files and the progress. */
export class DataRoomUploadTransfer {
  static createLimiter(max: number): TDataRoomLimiter {
    let active = 0;
    const waiting: (() => void)[] = [];
    return async <T>(task: () => Promise<T>): Promise<T> => {
      if (active < max) active += 1;
      else
        await new Promise<void>((resolve) => {
          waiting.push(resolve);
        });
      try {
        return await task();
      } finally {
        // The slot passes straight to the next waiter, so a new caller can never slip in between.
        const next = waiting.shift();
        if (next) next();
        else active -= 1;
      }
    };
  }

  static async runPool<T>(
    items: readonly T[],
    limit: number,
    worker: (item: T) => Promise<void>
  ): Promise<void> {
    let nextIndex = 0;
    const lane = async (): Promise<void> => {
      if (nextIndex >= items.length) return;
      const item = items[nextIndex];
      nextIndex += 1;
      await worker(item);
      await lane();
    };
    await Promise.all(
      Array.from({ length: Math.min(limit, items.length) }, () => lane())
    );
  }

  /** Creates the upload with the first batch, then appends the rest; the resume record is written
   * after every batch. Any failure part way aborts the upload so nothing is left half open. */
  static async createRoom({
    uploads,
    limits,
    conversationId,
    companyId,
    signal,
    onRecordChange,
  }: Readonly<ICreateDataRoomParams>): Promise<TCreateDataRoomResult> {
    const [firstBatch, ...otherBatches] = DataRoomUploadHelper.splitBatches(
      uploads,
      limits.maxFilesPerRequest
    );
    if (!firstBatch)
      return { success: false, error: 'There are no files to upload.' };

    const created = await createDocumentReaderUpload(
      {
        conversationId,
        ...(companyId ? { companyId } : {}),
        files: firstBatch.map((upload) =>
          DataRoomUploadHelper.toFileInput(upload)
        ),
      },
      signal
    );
    if (!created.success || !created.data)
      return {
        success: false,
        error: created.error ?? 'Could not start the data room upload.',
      };

    const uploadId = created.data.upload.id;
    const firstFiles = DataRoomUploadHelper.pairCreatedFiles(
      firstBatch,
      created.data.upload
    );
    if (!firstFiles)
      return DataRoomUploadTransfer.stopCreate(uploadId, PAIRING_ERROR);

    const room: IDataRoomCreateState = {
      files: firstFiles,
      skippedEmptyFileNames: created.data.upload.skippedEmptyFileNames,
      lastModifiedByBatch: [firstBatch.map(({ file }) => file.lastModified)],
      error: null,
    };
    onRecordChange({
      uploadId,
      fileCount: uploads.length,
      lastModifiedByBatch: room.lastModifiedByBatch,
    });

    await otherBatches.reduce(async (previous, batch) => {
      await previous;
      if (room.error) return;
      const appended = await appendDocumentReaderUploadFiles(
        uploadId,
        batch.map((upload) => DataRoomUploadHelper.toFileInput(upload)),
        signal
      );
      const files =
        appended.success && appended.data
          ? DataRoomUploadHelper.pairCreatedFiles(batch, appended.data.upload)
          : null;
      if (!appended.data || !files) {
        room.error = appended.error ?? PAIRING_ERROR;
        return;
      }
      room.files = [...room.files, ...files];
      room.skippedEmptyFileNames = [
        ...room.skippedEmptyFileNames,
        ...appended.data.upload.skippedEmptyFileNames,
      ];
      room.lastModifiedByBatch = [
        ...room.lastModifiedByBatch,
        batch.map(({ file }) => file.lastModified),
      ];
      onRecordChange({
        uploadId,
        fileCount: uploads.length,
        lastModifiedByBatch: room.lastModifiedByBatch,
      });
    }, Promise.resolve());

    if (room.error)
      return DataRoomUploadTransfer.stopCreate(uploadId, room.error);
    return {
      success: true,
      uploadId,
      files: room.files,
      skippedEmptyFileNames: room.skippedEmptyFileNames,
    };
  }

  /** Uploads files a few at a time and reports each as it settles. */
  static async uploadFiles(
    context: Readonly<IDataRoomTransferContext>,
    files: readonly IDataRoomLocalFile[],
    onFileSettled: (
      file: IDataRoomLocalFile,
      outcome: IDataRoomFileOutcome
    ) => void
  ): Promise<void> {
    await DataRoomUploadTransfer.runPool(
      files,
      DataRoomUploadHelper.PARTS_IN_FLIGHT,
      async (file) => {
        if (context.signal.aborted) return;
        onFileSettled(
          file,
          await DataRoomUploadTransfer.uploadFile(context, file)
        );
      }
    );
  }

  /** Sends only the parts S3 does not hold yet, then completes the file with its part list. */
  static async uploadFile(
    context: Readonly<IDataRoomTransferContext>,
    file: IDataRoomLocalFile
  ): Promise<IDataRoomFileOutcome> {
    const progress = {
      storedBytes: DataRoomUploadHelper.storedBytes(file),
      inFlight: new Map<number, number>(),
    };
    const report = (partNumber: number, loadedBytes: number) => {
      if (loadedBytes > 0) progress.inFlight.set(partNumber, loadedBytes);
      else progress.inFlight.delete(partNumber);
      const inFlightBytes = [...progress.inFlight.values()].reduce(
        (sum, bytes) => sum + bytes,
        0
      );
      context.onFileBytes(file.fileId, progress.storedBytes + inFlightBytes);
    };
    const failure: { outcome: { error: string; isFatal: boolean } | null } = {
      outcome: null,
    };
    const isStopped = () =>
      context.signal.aborted || failure.outcome?.isFatal === true;

    report(0, 0);
    const chunks = DataRoomUploadHelper.splitBatches(
      DataRoomUploadHelper.missingPartNumbers(file),
      context.maxPartUrlsPerRequest
    );
    await chunks.reduce(async (previous, partNumbers) => {
      await previous;
      if (isStopped()) return;
      const urls = await requestDocumentReaderPartUrls(
        context.uploadId,
        file.fileId,
        partNumbers,
        context.signal
      );
      if (!urls.success || !urls.data) {
        failure.outcome = failure.outcome ?? {
          error: urls.error ?? 'Could not prepare this file.',
          isFatal: false,
        };
        return;
      }
      const urlByPart = new Map(
        urls.data.partUrls.map((partUrl) => [partUrl.partNumber, partUrl.url])
      );
      await DataRoomUploadTransfer.runPool(
        partNumbers,
        DataRoomUploadHelper.PARTS_IN_FLIGHT_PER_FILE,
        async (partNumber) => {
          if (isStopped()) return;
          const outcome = await DataRoomUploadTransfer.sendPart(
            context,
            file,
            partNumber,
            urlByPart.get(partNumber) ?? null,
            0,
            report
          );
          if ('entityTag' in outcome) {
            file.storedEntityTags.set(partNumber, outcome.entityTag);
            const range = DataRoomUploadHelper.partRange(
              partNumber,
              file.selectedFile.file.size,
              file.partSizeBytes
            );
            progress.storedBytes += range.end - range.start;
            report(partNumber, 0);
          } else if (!failure.outcome || outcome.isFatal)
            failure.outcome = outcome;
        }
      );
    }, Promise.resolve());

    if (context.signal.aborted)
      return {
        success: false,
        isFatal: true,
        uploadStatus: null,
        error: CANCELLED_ERROR,
      };
    if (failure.outcome)
      return {
        success: false,
        isFatal: failure.outcome.isFatal,
        uploadStatus: null,
        error: failure.outcome.error,
      };

    const parts = [...file.storedEntityTags.entries()]
      .sort(([first], [second]) => first - second)
      .map(([partNumber, entityTag]) => ({ partNumber, entityTag }));
    const completed = await completeDocumentReaderUploadFile(
      context.uploadId,
      file.fileId,
      parts,
      context.signal
    );
    if (!completed.success || !completed.data) {
      // The backend's part list disagrees with ours, so a retry starts from what S3 says it holds.
      if (completed.status === 400)
        await DataRoomUploadTransfer.reloadStoredParts(context, file);
      return {
        success: false,
        isFatal: false,
        uploadStatus: null,
        error: completed.error ?? 'Could not finish this file.',
      };
    }
    return {
      success: true,
      isFatal: false,
      uploadStatus: completed.data.upload.status,
      error: null,
    };
  }

  /** Reads every batch of an unfinished upload and pairs the server's files with the re-selected ones. */
  static async loadResume(
    record: Readonly<IDataRoomResumeRecord>,
    uploads: readonly CommendaAiUploadFile[],
    signal: AbortSignal
  ): Promise<TLoadDataRoomResumeResult> {
    const first = await getDocumentReaderUploadBatch(
      record.uploadId,
      0,
      signal
    );
    if (!first.success || !first.data)
      return {
        success: false,
        status: null,
        error: first.error ?? 'Could not read the data room upload.',
      };
    const { status, fileCount, batchCount } = first.data.upload;
    if (status === DocumentReaderUploadStatus.ABORTED)
      return {
        success: false,
        status,
        error:
          'This data room upload was cancelled, so it cannot be finished. Upload it again.',
      };
    if (fileCount < record.fileCount)
      return {
        success: false,
        status,
        error:
          'This upload stopped before all its files were added. Cancel it and upload the data room again.',
      };

    const result: IDataRoomResumeMatchResult = {
      files: [],
      storedRows: [],
      unmatchedNames: [],
      abortedNames: [],
    };
    const batchIndexes = Array.from(
      { length: batchCount },
      (_, index) => index
    );
    const failure: { error: string | null } = { error: null };
    await batchIndexes.reduce(async (previous, batchIndex) => {
      await previous;
      if (failure.error) return;
      const batch =
        batchIndex === 0
          ? first
          : await getDocumentReaderUploadBatch(
              record.uploadId,
              batchIndex,
              signal
            );
      if (!batch.success || !batch.data) {
        failure.error = batch.error ?? 'Could not read the data room upload.';
        return;
      }
      const serverFiles = batch.data.upload.files;
      const pending = serverFiles.filter(
        (serverFile) =>
          serverFile.status === DocumentReaderUploadFileStatus.UPLOADING
      );
      const match = DataRoomUploadHelper.matchResumeFiles(
        uploads,
        pending,
        batchIndex,
        record.lastModifiedByBatch[batchIndex]
      );
      result.files.push(...match.files);
      result.unmatchedNames.push(...match.unmatchedNames);
      serverFiles.forEach((serverFile) => {
        if (serverFile.status === DocumentReaderUploadFileStatus.COMPLETED)
          result.storedRows.push(DataRoomUploadHelper.toStoredRow(serverFile));
        if (serverFile.status === DocumentReaderUploadFileStatus.ABORTED)
          result.abortedNames.push(
            serverFile.relativePath ?? serverFile.fileName
          );
      });
    }, Promise.resolve());

    if (failure.error) return { success: false, status, error: failure.error };
    return { success: true, status, ...result };
  }

  static async fetchUploadStatus(
    uploadId: string
  ): Promise<DocumentReaderUploadStatus | null> {
    const read = await getDocumentReaderUploadBatch(uploadId, 0);
    return read.success && read.data ? read.data.upload.status : null;
  }

  /** Replaces the file's part list with the parts S3 holds; if the batch cannot be read, every part
   * is sent again. */
  private static async reloadStoredParts(
    context: Readonly<IDataRoomTransferContext>,
    file: IDataRoomLocalFile
  ): Promise<void> {
    const batch = await getDocumentReaderUploadBatch(
      context.uploadId,
      file.batchIndex,
      context.signal
    );
    const serverFile = batch.data?.upload.files.find(
      (candidate) => candidate.id === file.fileId
    );
    file.storedEntityTags.clear();
    serverFile?.uploadedParts.forEach((part) =>
      file.storedEntityTags.set(part.partNumber, part.entityTag)
    );
  }

  private static async stopCreate(
    uploadId: string,
    error: string
  ): Promise<TCreateDataRoomResult> {
    await abortDocumentReaderUpload(uploadId);
    return { success: false, error };
  }

  /** One part with up to three retries, each after a short wait (and after the browser is back
   * online) with a fresh URL. A 2xx without an ETag is never retried: the bucket CORS hides it. */
  private static async sendPart(
    context: Readonly<IDataRoomTransferContext>,
    file: Readonly<IDataRoomLocalFile>,
    partNumber: number,
    url: string | null,
    attempt: number,
    report: (partNumber: number, loadedBytes: number) => void
  ): Promise<TPartOutcome> {
    const range = DataRoomUploadHelper.partRange(
      partNumber,
      file.selectedFile.file.size,
      file.partSizeBytes
    );
    const result: TPutDocumentReaderPartResult = url
      ? await context.limitPart(() =>
          putDocumentReaderPart({
            url,
            blob: file.selectedFile.file.slice(range.start, range.end),
            signal: context.signal,
            onProgress: (loadedBytes) => report(partNumber, loadedBytes),
          })
        )
      : {
          success: false,
          status: 0,
          error: `Could not get a new link for part ${partNumber}.`,
        };
    report(partNumber, 0);

    if (result.success === true) return { entityTag: result.entityTag };
    if (context.signal.aborted)
      return { error: CANCELLED_ERROR, isFatal: true };
    if (result.status >= 200 && result.status < 300)
      return { error: result.error, isFatal: true };

    const delayMs = DataRoomUploadHelper.RETRY_DELAYS_MS[attempt];
    if (delayMs === undefined) return { error: result.error, isFatal: false };
    await DataRoomUploadTransfer.wait(delayMs, context.signal);
    await DataRoomUploadTransfer.waitUntilOnline(context.signal);
    if (context.signal.aborted)
      return { error: CANCELLED_ERROR, isFatal: true };

    const fresh = await requestDocumentReaderPartUrls(
      context.uploadId,
      file.fileId,
      [partNumber],
      context.signal
    );
    const freshUrl =
      fresh.data?.partUrls.find((partUrl) => partUrl.partNumber === partNumber)
        ?.url ?? null;
    return DataRoomUploadTransfer.sendPart(
      context,
      file,
      partNumber,
      freshUrl,
      attempt + 1,
      report
    );
  }

  private static wait(delayMs: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve) => {
      const timer = setTimeout(resolve, delayMs);
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timer);
          resolve();
        },
        { once: true }
      );
    });
  }

  /** An offline browser fails every PUT at once; waiting here keeps those failures from using up the
   * part's retries. */
  private static waitUntilOnline(signal: AbortSignal): Promise<void> {
    if (typeof navigator === 'undefined' || navigator.onLine)
      return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => {
        window.removeEventListener('online', done);
        resolve();
      };
      window.addEventListener('online', done);
      signal.addEventListener('abort', done, { once: true });
    });
  }
}
